import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Wrench, Database } from "lucide-react";
import { isNativePlatform, getPlatform } from "@/lib/platform";
import { AUTH_TOKEN_KEY } from "@/lib/api";
import { readTokenIDB } from "@/lib/persistentStorage";

const NativeDiagCard = () => {
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [storageLog, setStorageLog] = useState(null);
  const [storageRunning, setStorageRunning] = useState(false);

  const native = isNativePlatform();
  const plat = getPlatform();

  const runDiag = async () => {
    setRunning(true);
    setLog([]);
    try {
      const { setupNativePush, getNativePushDiagnostics } = await import("@/lib/nativePush");
      await setupNativePush();
      // Wait a couple of seconds so async events (registration, api response) get logged
      await new Promise((r) => setTimeout(r, 2500));
      setLog(getNativePushDiagnostics());
    } finally {
      setRunning(false);
    }
  };

  const runStorageDiag = async () => {
    setStorageRunning(true);
    const out = {
      ts: new Date().toLocaleTimeString(),
      localStorage: "?",
      preferences: "?",
      indexedDB: "?",
      cookieSession: "?",
    };
    // localStorage (sync)
    try {
      const v = localStorage.getItem(AUTH_TOKEN_KEY);
      out.localStorage = v ? `SI (len=${v.length}, ...${v.slice(-6)})` : "VUOTO";
    } catch (e) {
      out.localStorage = `ERR: ${String(e).slice(0, 40)}`;
    }
    // Capacitor Preferences
    if (native) {
      try {
        const mod = await import("@capacitor/preferences");
        const P = mod.Preferences;
        const { value } = await P.get({ key: AUTH_TOKEN_KEY });
        out.preferences = value ? `SI (len=${value.length}, ...${value.slice(-6)})` : "VUOTO";
      } catch (e) {
        out.preferences = `ERR: ${String(e).slice(0, 40)}`;
      }
    } else {
      out.preferences = "N/A (web)";
    }
    // IndexedDB
    try {
      const v = await readTokenIDB();
      out.indexedDB = v ? `SI (len=${v.length}, ...${v.slice(-6)})` : "VUOTO";
    } catch (e) {
      out.indexedDB = `ERR: ${String(e).slice(0, 40)}`;
    }
    // Cookie session
    try {
      const m = document.cookie.match(/session_token=([^;]+)/);
      out.cookieSession = m ? `SI (...${m[1].slice(-6)})` : "VUOTO o HttpOnly";
    } catch (e) {
      out.cookieSession = `ERR: ${String(e).slice(0, 40)}`;
    }
    setStorageLog(out);
    setStorageRunning(false);
  };

  return (
    <Card
      className={`border-2 ${
        native ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50"
      }`}
      data-testid="diag-mode-card"
    >
      <CardContent className="pt-4 text-sm space-y-3">
        <div>
          <div className="font-semibold mb-1">
            {native ? "🟢 MODALITÀ NATIVA" : "🔵 MODALITÀ WEB / PWA"}
          </div>
          <div className="text-xs text-muted-foreground">
            Platform: <strong>{plat}</strong> · Native: <strong>{native ? "SÌ" : "no"}</strong>
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">
            {native
              ? "L'app nativa è attiva — APNs disponibile."
              : "Sei nella versione web. Le notifiche native APNs funzionano solo dentro l'app installata Hello Revela."}
          </div>
        </div>

        {native && (
          <div className="pt-2 border-t border-emerald-200">
            <Button
              size="sm"
              variant="outline"
              onClick={runDiag}
              disabled={running}
              className="w-full mb-2"
              data-testid="diag-push-btn"
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Diagnosi in corso…
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4 mr-2" />
                  Diagnosi notifiche push
                </>
              )}
            </Button>

            {log.length > 0 && (
              <div className="max-h-72 overflow-y-auto bg-white rounded p-2 border border-emerald-200">
                <div className="text-[10px] font-mono space-y-0.5">
                  {log.map((entry, i) => (
                    <div key={i} className="break-all">
                      <span className="text-muted-foreground">{entry.ts}</span>{" "}
                      <span className="font-semibold">{entry.step}</span>
                      {entry.info && <span className="text-muted-foreground"> · {entry.info}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 pt-2 border-t border-emerald-200">
              <Button
                size="sm"
                variant="outline"
                onClick={runStorageDiag}
                disabled={storageRunning}
                className="w-full mb-2"
                data-testid="diag-storage-btn"
              >
                {storageRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifico storage…
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Diagnosi storage login
                  </>
                )}
              </Button>
              {storageLog && (
                <div className="bg-white rounded p-2 border border-emerald-200 text-[11px] font-mono space-y-1">
                  <div className="text-muted-foreground">{storageLog.ts}</div>
                  <div>
                    <span className="font-semibold">localStorage:</span>{" "}
                    <span className={storageLog.localStorage.startsWith("SI") ? "text-emerald-700" : "text-red-600"}>
                      {storageLog.localStorage}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Preferences (Keychain):</span>{" "}
                    <span className={storageLog.preferences.startsWith("SI") ? "text-emerald-700" : "text-red-600"}>
                      {storageLog.preferences}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">IndexedDB:</span>{" "}
                    <span className={storageLog.indexedDB.startsWith("SI") ? "text-emerald-700" : "text-red-600"}>
                      {storageLog.indexedDB}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Cookie:</span>{" "}
                    <span className={storageLog.cookieSession.startsWith("SI") ? "text-emerald-700" : "text-amber-600"}>
                      {storageLog.cookieSession}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NativeDiagCard;
